import { docEndMs } from './documents';
/** Nigerian phone numbers are stored as 234 + 10 digits (first digit 7, 8 or 9, then 0 or 1). */
export const phoneOk = (p: string): boolean => /^234[789][01]\d{8}$/.test(p);

/** The payout account number is the last 10 digits of the phone number. */
export const accountOf = (phone: string): string => phone.slice(-10);

export const normalizeName = (s: string): string[] =>
  s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/[^a-z ]/g, ' ').split(/\s+/).filter(Boolean);

/**
 * The bank account name must match the name on the app. Word order and extra middle names are fine:
 * every word of the app name has to appear in the bank name, and the app name needs at least two words.
 */
export function nameMatches(appName: string, bankName: string): boolean {
  const a = normalizeName(appName), b = normalizeName(bankName);
  return new Set(a).size > 1 && a.every((w) => b.includes(w)); // one word repeated is still one word
}

/** A vehicle number is plain text, whatever the plate looks like: it only has to be there and fit the box. */
export const PLATE_MAX = 20;
export const plateOk = (p: string): boolean => p.trim().length > 0 && p.trim().length <= PLATE_MAX;

/** A document date is still valid until the end of that day in Lagos (see documents.ts). */
export function notExpired(isoDate: string, now: Date = new Date()): boolean {
  const end = docEndMs(isoDate);
  return Number.isFinite(end) && end > now.getTime();
}

export const BANKS = [
  'Access Bank', 'Citibank', 'Ecobank', 'Fidelity Bank', 'First Bank', 'FCMB', 'Globus Bank', 'GTBank', 'Heritage Bank',
  'Keystone Bank', 'Kuda', 'Moniepoint', 'OPay', 'PalmPay', 'Polaris Bank', 'Providus Bank', 'Stanbic IBTC',
  'Sterling Bank', 'Union Bank', 'UBA', 'Unity Bank', 'Wema Bank / ALAT', 'Zenith Bank',
] as const;

/** A 6-digit PIN that is not trivially guessable: not all one digit, not a straight run (123456 / 654321), not a repeated pair (121212). */
export function pinOk(pin: string): boolean {
  if (!/^\d{6}$/.test(pin)) return false;
  if (/^(\d)\1{5}$/.test(pin)) return false;
  if ('0123456789'.includes(pin) || '9876543210'.includes(pin)) return false;
  if (/^(\d{2})\1{2}$/.test(pin) || /^(\d{3})\1$/.test(pin)) return false;
  return !['123123', '112233', '000111', '111222', '654321', '121212'].includes(pin);
}
